import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="mt-12 first:mt-0">
    <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight text-white mb-4">{title}</h2>
    <div className="space-y-4 text-white/75 font-body leading-relaxed text-base">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-5 list-disc marker:text-[#3B82F6]">
    {items.map((i, idx) => <li key={idx}>{i}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="pt-28 md:pt-36 pb-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-6">Legal</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
          Privacy Policy
        </h1>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Last updated: 28 November 2024
        </p>

        <div className="mt-16 border-t border-white/10 pt-12">
          <Section title="Introduction">
            <p>
              Switch Tech Talent Ltd ("Switch Tech Talent", "we", "our" or "us") is committed to
              protecting your privacy and handling your personal information responsibly.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, store and share your personal
              information when you engage with our recruitment services, visit our website or
              communicate with us.
            </p>
            <p>
              We process personal data in accordance with the UK General Data Protection
              Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </Section>

          <Section title="What Information Do We Collect?">
            <p>We may collect:</p>
            <Bullets items={[
              "Name and contact details",
              "CV, employment history and qualifications",
              "Salary or rate expectations",
              "Right to work information",
              "Interview notes and recruitment-related communications",
              "Information provided through our website, forms or applications",
              "Information available from professional networking sites such as LinkedIn",
              "Website usage information, including IP address, browser type and device information",
            ]} />
          </Section>

          <Section title="How Do We Collect Information?">
            <p>We may collect information when:</p>
            <Bullets items={[
              "You apply for a role",
              "You submit a CV to us",
              "You register on our website",
              "You contact one of our consultants",
              "We identify your profile through professional networking sites, job boards or referrals",
              "You engage with our website",
            ]} />
          </Section>

          <Section title="How Do We Use Your Information?">
            <p>We use your information to:</p>
            <Bullets items={[
              "Match you with suitable job opportunities",
              "Introduce you to prospective employers",
              "Manage recruitment processes and applications",
              "Verify identity and right to work where required",
              "Maintain candidate records",
              "Improve our recruitment services",
              "Comply with legal and regulatory obligations",
            ]} />
            <p>We will not submit your CV or personal information to a client without your prior consent.</p>
          </Section>

          <Section title="Lawful Basis for Processing">
            <p>We process personal data using one or more of the following lawful bases:</p>
            <Bullets items={[
              "Legitimate Interests",
              "Consent",
              "Contractual Necessity",
              "Legal Obligations",
            ]} />
            <p>
              As a recruitment business, we maintain candidate records to support current and future
              employment opportunities and to meet our contractual and regulatory obligations.
            </p>
          </Section>

          <Section title="Sharing Your Information">
            <p>We may share your information with:</p>
            <Bullets items={[
              "Clients considering you for employment opportunities",
              "Service providers supporting our recruitment operations",
              "Professional advisers and regulatory authorities where required by law",
            ]} />
            <p>We do not sell personal information to third parties.</p>
            <p>
              No mobile information will be shared with third parties or affiliates for marketing
              or promotional purposes.
            </p>
          </Section>

          <Section title="Marketing Communications">
            <p>
              We may contact you regarding relevant employment opportunities, market insights and
              recruitment-related information.
            </p>
            <p>
              You can opt out of marketing communications at any time by contacting us at{" "}
              <a href="mailto:hello@switchtt.co.uk" className="text-[#3B82F6] hover:text-white underline underline-offset-4">hello@switchtt.co.uk</a>.
            </p>
            <p>
              Candidates actively engaged in recruitment processes may continue to receive
              communications necessary to manage those processes.
            </p>
          </Section>

          <Section title="Data Retention">
            <p>
              We retain candidate data for up to three years from our last meaningful contact
              unless a longer retention period is required by law or necessary for legitimate
              business purposes.
            </p>
            <p>
              You may request deletion of your information at any time, subject to any legal or
              contractual obligations requiring retention.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>You have the right to:</p>
            <Bullets items={[
              "Access your personal information",
              "Correct inaccurate information",
              "Request deletion of your information",
              "Restrict or object to processing",
              "Request transfer of your information",
              "Withdraw consent where applicable",
            ]} />
            <p>To exercise your rights, please contact us using the details below.</p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have any questions regarding this Privacy Policy or the information we hold
              about you, please contact:
            </p>
            <div className="border-l-2 border-[#3B82F6] pl-5 py-1 mt-4">
              <p className="font-display font-bold text-white">Switch Tech Talent Ltd</p>
              <p>Email: <a href="mailto:hello@switchtt.co.uk" className="text-[#3B82F6] hover:text-white underline underline-offset-4">hello@switchtt.co.uk</a></p>
              <p>Website: <a href="https://www.switchtt.co.uk" className="text-[#3B82F6] hover:text-white underline underline-offset-4">www.switchtt.co.uk</a></p>
            </div>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any updates will be published
              on this page.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-10 border-t border-white/10">
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-[#3B82F6]">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
